import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import I18n from '@/modules/i18n';
import * as Routes from '@/modules/routes';
import AIBadge from '@/components/shared/AIBadge';
import showLevelingStatusToast from '@/components/shared/SectionAIGeneratingPanel/showLevelingStatusToast';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { COLORS } from '@/components/ui/ui.constants';
import cn from '@/utils/cn';
import useReadingLevelGeneratingStore from '@/stores/useReadingLevelGeneratingStore';

// AIDEV-NOTE: First poll fires immediately; subsequent polls after this delay.
const POLL_INTERVAL_MS = 2000;

interface AIReadingLevelGeneratingProps {
  generatePath: string;
  gradeBand: string;
  /** Called with the error message when generation fails. When provided, suppresses inline error rendering. */
  onError?: (message: string) => void;
  /** Called on success instead of page reload. Use in contexts that manage content themselves. */
  onSuccess?: () => void;
  programId?: number;
  /** When false, suppresses the in-content loading spinner (e.g. when spinner lives in the dropdown button instead). */
  showLoadingSpinner?: boolean;
  sectionId: number;
}

type GenerationState = 'loading' | 'error';

// AIDEV-NOTE: PLAT-16897 — Shown when a teacher selects an AI grade band with no cached content.
// Fires generate_ai_reading_level (async), then polls job_statuses/:id every 2s until the
// Sidekiq worker completes. On success: calls onSuccess (big book) or reloads (reading page).
// On failure: shows error banner + retry. Does NOT silently fall back (PLAT-16897 req 5).
const AIReadingLevelGenerating = ({
  generatePath,
  gradeBand,
  onError,
  onSuccess,
  programId,
  sectionId,
  showLoadingSpinner = true,
}: AIReadingLevelGeneratingProps) => {
  const [state, setState] = useState<GenerationState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // cancelledRef guards against state updates after unmount or retry cancellation.
  const cancelledRef = useRef(false);

  const stopPolling = () => {
    cancelledRef.current = true;
    if (pollTimeoutRef.current !== null) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const handleSuccess = () => {
    stopPolling();
    if (onSuccess) {
      onSuccess();
    } else {
      window.location.reload();
    }
  };

  const handleRateLimited = () => {
    stopPolling();
    setIsRateLimited(true);
    const store = useReadingLevelGeneratingStore.getState();
    store.setRateLimited(true);
    store.clearGenerating();
    showLevelingStatusToast(
      <Icon className="tw-shrink-0" color={COLORS.black} icon="InfoAlternate" size={24} />,
      I18n.t('ai_daily_limit_reached'),
    );
    if (!onError) {
      setErrorMessage(I18n.t('ai_daily_limit_reached'));
      setState('error');
    }
  };

  const handleError = (message: string) => {
    stopPolling();
    if (onError) {
      onError(message);
    } else {
      setErrorMessage(message);
      setState('error');
    }
  };

  const startPolling = (jobId: string) => {
    // Recursive setTimeout so the first poll fires immediately with no delay,
    // then subsequent polls wait POLL_INTERVAL_MS.
    const poll = async () => {
      if (cancelledRef.current) return;
      try {
        const { data } = await axios.get(Routes.plato_api_job_status_path(jobId));
        if (cancelledRef.current) return;
        const status = data?.data?.status;
        if (status === 'completed') {
          handleSuccess();
        } else if (status === 'rate_limited') {
          handleRateLimited();
        } else if (status === 'failed' || status === 'not_found') {
          handleError(data?.data?.error?.message ?? I18n.t('ai_reading_level_failed'));
        } else {
          pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        if (!cancelledRef.current) handleError(I18n.t('ai_reading_level_failed'));
      }
    };
    poll();
  };

  const generate = () => {
    stopPolling();
    cancelledRef.current = false;
    setState('loading');
    setErrorMessage(null);

    axios
      .post(generatePath, { grade_band: gradeBand, program_id: programId })
      .then(({ data }) => {
        if (data.status === 'completed') {
          handleSuccess();
        } else if (data.status === 'rate_limited') {
          handleRateLimited();
        } else if (data.job_id) {
          startPolling(data.job_id);
        } else {
          handleError(I18n.t('ai_reading_level_failed'));
        }
      })
      .catch((err) => {
        handleError(err.response?.data?.error?.message ?? I18n.t('ai_reading_level_failed'));
      });
  };

  useEffect(() => {
    generate();
    return stopPolling;
  }, [sectionId, gradeBand]);

  if (state === 'loading') {
    if (!showLoadingSpinner) return null;
    return (
      <div
        aria-live="polite"
        className={cn(
          'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4',
          'tw-py-16 tw-text-center',
        )}
        data-testid="ai-reading-level-generating"
      >
        <AIBadge />
        <i aria-hidden className="fa fa-spinner fa-spin fa-2x tw-text-tci-purple" />
        <Text className="tw-text-spa-secondary" size="sm">{I18n.t('ai_reading_level_generating')}</Text>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'tw-flex tw-flex-col tw-items-start tw-gap-3',
        'tw-p-4 tw-rounded-lg tw-bg-yellow-50 tw-border tw-border-yellow-300',
      )}
      data-testid="ai-reading-level-error"
      role="alert"
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <AIBadge />
        <Text size="sm" weight="semibold">{errorMessage ?? I18n.t('ai_reading_level_failed')}</Text>
      </div>
      {!isRateLimited && (
        <button
          className={cn(
            'tw-px-3 tw-py-1.5 tw-rounded tw-border tw-border-solid tw-border-tci-purple',
            'tw-text-tci-purple tw-text-sm tw-cursor-pointer tw-bg-white',
            'hover:tw-bg-tci-purple hover:tw-text-white',
          )}
          type="button"
          onClick={generate}
        >
          {I18n.t('ai_reading_level_retry')}
        </button>
      )}
    </div>
  );
};

export default AIReadingLevelGenerating;
